import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IBlackboardEntry, IChecklistItem, IChecklistItemCompletion, ISchedulerTask, SiteId, TaskId } from '@interfaces/IScheduler';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useCurrentActor } from '@hooks/useCurrentActor';
import { asksForEvidence, checklistProgress, type IChecklistProgress } from '@d-orca/shared/checklist';
import { projectToday } from '@d-orca/shared/client/activities';
import { blackboardDefaults } from '../lib/checklistBlackboard';
import { useSchedulerActions, useSchedulerStale, useSchedulerTask } from './useSchedulerStore';
import { useTaskChecklistPersist, type ICheckEvidence } from './useTaskChecklistPersist';
import type { IEvidenceTarget } from './useChecklistPhotoUpload';

// ---------------------------------------------------------------------------
// The checklist as it is ticked (task-checklist design, decisions 7–10): the
// LIVE task's items in order, the done/total count, and every way a tick
// happens — straight, or through a capture step when the item asks for
// evidence. Ticks are execution: optimistic in the store, then one immediate
// PUT per item; one the server refuses for good is rolled back from the
// server copy and reported until the next tick.
// ---------------------------------------------------------------------------

export interface IChecklistTicks {
  task: ISchedulerTask | undefined;
  /** The checklist in the order it is read on site. */
  items: IChecklistItem[];
  progress: IChecklistProgress;
  /** Committed and not behind the upstream copy. */
  tickable: boolean;
  /** Tick or untick — through the capture step when the item asks for evidence. */
  toggle: (item: IChecklistItem, checked: boolean) => void;
  /** The item whose evidence is being captured, if any. */
  capturingId: string | null;
  /** The captured tick, made. */
  commitCapture: (itemId: string, completion: IChecklistItemCompletion) => void;
  cancelCapture: () => void;
  /** The capture step's 黒板, filled in from the task. */
  blackboard: Required<IBlackboardEntry>;
  /** Where a captured photo is stored — absent without a loaded schedule. */
  evidenceTarget: (itemId: string) => IEvidenceTarget | undefined;
  /** Who a tick made here is recorded as, until the server's copy arrives. */
  checkedByName: string;
  /** The last tick was refused by the server (and rolled back). */
  refused: boolean;
}

/** The client evidence a completion carries — for the granular `checks` PUT. */
function evidenceOf(completion: IChecklistItemCompletion): ICheckEvidence | undefined {
  const { attachedPhoto, comment, blackboard } = completion;
  const evidence: ICheckEvidence = {
    ...(attachedPhoto ? { attachedPhoto } : {}),
    ...(comment !== undefined ? { comment } : {}),
    ...(blackboard ? { blackboard } : {}),
  };
  return Object.keys(evidence).length > 0 ? evidence : undefined;
}

export function useChecklistTicks(
  siteId: SiteId,
  taskId: TaskId,
  scheduleNumber: string | undefined,
  tz: string | undefined,
): IChecklistTicks {
  const { t } = useTranslation();
  const { taskChecklistItemToggled } = useSchedulerActions();
  const actor = useCurrentActor();
  // The LIVE task: ticks land immediately, a draft snapshot would lag them.
  const task = useSchedulerTask(siteId, taskId);
  const stale = useSchedulerStale(siteId);
  const { floors } = useSiteLocations(siteId);
  const [refused, setRefused] = useState(false);
  const onRefused = useCallback(() => setRefused(true), []);
  const persist = useTaskChecklistPersist(siteId, scheduleNumber, taskId, onRefused);
  const [capturingId, setCapturingId] = useState<string | null>(null);

  const items = [...(task?.checklist ?? [])].sort((a, b) => a.order - b.order);

  const commit = (itemId: string, checked: boolean, completion?: IChecklistItemCompletion) => {
    taskChecklistItemToggled({
      siteId,
      taskId,
      itemId,
      checked,
      ...(completion ? { completion } : {}),
      activityId: `act-${crypto.randomUUID()}`,
      at: new Date().toISOString(),
      by: actor.id,
    });
    persist(itemId, checked, completion ? evidenceOf(completion) : undefined);
  };

  const toggle = (item: IChecklistItem, checked: boolean) => {
    setRefused(false);
    if (!checked) commit(item.id, false);
    else if (asksForEvidence(item)) setCapturingId(item.id);
    else commit(item.id, true, { checked: true, checkedAt: new Date().toISOString(), checkedByName: actor.name });
  };

  return {
    task,
    items,
    progress: checklistProgress(items),
    tickable: !!task?.committed && !stale,
    toggle,
    capturingId,
    commitCapture: (itemId, completion) => {
      commit(itemId, true, completion);
      setCapturingId(null);
    },
    cancelCapture: () => setCapturingId(null),
    blackboard: blackboardDefaults(t, task ?? {}, floors, tz ? projectToday(tz) : ''),
    evidenceTarget: (itemId) => (scheduleNumber ? { siteId, scheduleNumber, taskId, itemId } : undefined),
    checkedByName: actor.name,
    refused,
  };
}
